import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

/**
 * LandscapeChartSheet — график во весь экран.
 *
 * Ограничение телефона в разборе — ШИРИНА строки, а не объём экрана: лента
 * узких секций несёт сколько угодно, но график в 358 доступных пикселей
 * действительно тесен. Повёрнутый телефон даёт 844×390 — полноценный холст,
 * шире, чем колонка на десктопе.
 *
 * Поэтому мы не упрощаем кривые для мобильного и не прячем их за «полной
 * версией». Мы даём тот же график во весь экран, а поворот делает его широким.
 * Заставить телефон повернуться из веба нельзя (Screen Orientation API
 * заблокирован вне полноэкранного режима на iOS), и мы не пытаемся: подсказка
 * появляется только в портрете и исчезает сама, когда экран повернули.
 */
export function LandscapeChartSheet({
  open,
  onClose,
  title,
  actions,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /**
   * Органы управления в шапке развёрнутого вида.
   *
   * До этого в ландшафте была ровно одна кнопка — «Закрыть». Развернул
   * график во всю ширину, и единственное доступное действие — выйти обратно:
   * ни настроить, ни переключить. Слот, а не готовая кнопка, потому что
   * набор действий знает вызывающий, а лист — нет.
   */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation("workout");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Фон не должен прокручиваться под открытым графиком.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-bg-base"
      data-testid="landscape-chart-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex items-center justify-between px-4 py-2 shrink-0">
        <span className="label-caps text-fg-muted truncate">{title}</span>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          <button
            type="button"
            onClick={onClose}
            data-testid="landscape-chart-close"
            className="px-3 h-9 rounded-md text-fg-secondary hover:text-fg-primary bg-bg-card"
          >
            {t("chart.close", { defaultValue: "Закрыть" })}
          </button>
        </div>
      </div>

      {/* Подсказка о повороте живёт только в портрете — в ландшафте она уже
          неуместна, поэтому прячется чистым CSS, без слушателя ориентации. */}
      <div
        className="px-4 pb-1 text-[11px] text-fg-muted landscape:hidden"
        data-testid="rotate-hint"
      >
        {t("chart.rotate_hint", {
          defaultValue: "Поверни телефон — график станет шире",
        })}
      </div>

      <div className="flex-1 min-h-0 px-2 pb-2">{children}</div>
    </div>,
    document.body,
  );
}
